import { useModal } from "@/features/modal/ModalStackManager";
import { Machine, MachineSearchBox } from "@/features/ui/PMS/machineSearchBox";
import { Button, Flex, Grid, Paper, Popover, Text } from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { IconCalendar, IconReportSearch } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";

// ✅ 차트 옵션 생성 함수
const chartOptions = (title: string, categories: string[]) => ({
  chart: { type: "bar" as const, toolbar: { show: false } },
  title: { text: title },
  xaxis: { categories },
  plotOptions: { bar: { borderRadius: 4, columnWidth: "50%" } },
  dataLabels: { enabled: false },
  yaxis: { labels: { formatter: (val: number) => `${val}%` } },
  tooltip: { y: { formatter: (val: number) => `${val}%` } },
});

// ✅ 연도/월별 고정 통계 (월: 6~7월만 / 연도: 2025년만 값 있음)
const fetchFixedStats = async (
  machine: Machine,
): Promise<{
  monthly: (number | null)[];
  yearly: (number | null)[];
}> => {
  const monthly = Array.from({ length: 12 }, (_, i) => {
    if (i === 5 || i === 6) {
      return Math.floor(Math.random() * 70) + 30;
    }
    return null;
  });

  const yearly = [null, null, null, null, Math.floor(Math.random() * 70) + 30]; // 2025년에만 값
  return { monthly, yearly };
};

// ✅ 일별 동적 통계 (6월, 7월만 허용)
const fetchDailyStats = async (
  machine: Machine,
  year: number,
  month: number,
): Promise<(number | null)[]> => {
  const today = new Date();
  const isFuture =
    year > today.getFullYear() || (year === today.getFullYear() && month > today.getMonth());
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // ✅ 6월과 7월만 데이터 허용
  if (month !== 5 && month !== 6) {
    return Array.from({ length: daysInMonth }, () => null);
  }

  return Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    if (isFuture || (isCurrentMonth && day > today.getDate())) return null;
    return Math.floor(Math.random() * 70) + 30;
  });
};

export default function CmsOperationPage() {
  const { openModal, closeModal } = useModal();
  const today = new Date();

  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [selectedYear, setSelectedYear] = useState(2025); // 2025 고정
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());

  const [dailySeries, setDailySeries] = useState<any[]>([]);
  const [monthlySeries, setMonthlySeries] = useState<any[]>([]);
  const [yearlySeries, setYearlySeries] = useState<any[]>([]);

  const [opened, { open, close }] = useDisclosure(false);

  const handleSelectMachine = (machine: Machine | null) => {
    if (machine) {
      setSelectedMachine(machine);
      closeModal("");
    }
  };

  useEffect(() => {
    if (selectedMachine) {
      fetchFixedStats(selectedMachine).then((res) => {
        setMonthlySeries([{ name: "월별 가동률", data: res.monthly }]);
        setYearlySeries([{ name: "연도별 가동률", data: res.yearly }]);
      });
    }
  }, [selectedMachine]);

  useEffect(() => {
    if (selectedMachine) {
      fetchDailyStats(selectedMachine, selectedYear, selectedMonth).then((daily) => {
        setDailySeries([{ name: "일별 가동률", data: daily }]);
      });
    }
  }, [selectedMachine, selectedYear, selectedMonth]);

  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const dayLabels = Array.from({ length: daysInMonth }, (_, i) => `${i + 1}일`);
  const monthLabels = Array.from({ length: 12 }, (_, i) => `${i + 1}월`);
  const yearLabels = Array.from({ length: 5 }, (_, i) => `${2021 + i}년`); // ✅ 2021~2025

  return (
    <Flex direction="column" p="md" gap="md">
      <Flex justify="space-between" mb="md">
        <Flex direction="column">
          <Text size="xl">CNC 설비</Text>
          {selectedMachine && (
            <Text size="sm" color="dimmed">
              선택된 설비: {selectedMachine.name}
            </Text>
          )}
        </Flex>

        <Flex gap="md">
          <Popover opened={opened} onClose={close} position="bottom-start" withArrow shadow="md">
            <Popover.Target>
              <Button leftIcon={<IconCalendar />} color="lime" onClick={open}>
                {`${selectedYear}년 ${selectedMonth + 1}월`}
              </Button>
            </Popover.Target>
            <Popover.Dropdown>
              <MonthPickerInput
                value={new Date(selectedYear, selectedMonth)}
                onChange={(value) => {
                  if (value) {
                    setSelectedYear(value.getFullYear());
                    setSelectedMonth(value.getMonth());
                    close();
                  }
                }}
                locale="ko"
                size="sm"
              />
            </Popover.Dropdown>
          </Popover>

          <Button
            onClick={() => {
              openModal(
                <MachineSearchBox type="CNC" onSelect={handleSelectMachine} />,
                null,
                "기계 검색",
              );
            }}
            leftIcon={<IconReportSearch />}
          >
            설비선택
          </Button>
        </Flex>
      </Flex>

      <Grid>
        <Grid.Col span={12}>
          <Paper p="md" radius="md" withBorder>
            <Chart
              options={chartOptions("일별", dayLabels)}
              series={dailySeries}
              type="bar"
              height={300}
            />
          </Paper>
        </Grid.Col>

        <Grid.Col span={8}>
          <Paper p="md" radius="md" withBorder>
            <Chart
              options={chartOptions("월별", monthLabels)}
              series={monthlySeries}
              type="bar"
              height={250}
            />
          </Paper>
        </Grid.Col>

        <Grid.Col span={4}>
          <Paper p="md" radius="md" withBorder>
            <Chart
              options={chartOptions("연도별", yearLabels)}
              series={yearlySeries}
              type="bar"
              height={250}
            />
          </Paper>
        </Grid.Col>
      </Grid>
    </Flex>
  );
}
